import { supabase } from "@/integrations/supabase/client";

/**
 * Process an image from an external URL and store it locally
 * @param imageUrl The URL of the image to process
 * @param nameHint A string hint for naming the image
 * @returns The local URL of the processed image
 */
export const processImage = async (imageUrl: string, nameHint: string): Promise<string> => {
  try {
    // Add debug logging to track image processing
    console.log(`Processing image: ${imageUrl}`);
    
    // Check if the URL is already a local URL
    if (!imageUrl) {
      console.log('No image URL provided');
      return '';
    }
    
    if (imageUrl.startsWith('/lovable-uploads/') || imageUrl.startsWith('/images/')) {
      console.log('Image already local, returning as is');
      return imageUrl;
    }

    // For high-quality Pexels images, keep the original URL
    // This ensures we maintain the high-quality images and proper attribution
    if (imageUrl.includes('images.pexels.com')) {
      console.log('Using original Pexels image URL');
      return imageUrl;
    }
    
    // Skip data URLs
    if (imageUrl.startsWith('data:')) {
      console.log('Skipping data URL');
      return imageUrl;
    }

    // Fix relative URLs by adding the base domain
    if (imageUrl.startsWith('/')) {
      // This is a relative URL, try to guess the domain from context
      // For now, just log a warning
      console.warn(`Found relative image URL: ${imageUrl}. Cannot process without base domain.`);
      return imageUrl;
    }

    // Clean up the name hint
    const cleanNameHint = nameHint
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .substring(0, 40) // Limit length
      .replace(/-{2,}/g, '-')
      .replace(/^-|-$/g, '');
      
    // Generate a filename - use the original extension if possible
    let extension = 'png';
    try {
      const urlObj = new URL(imageUrl);
      const pathnameParts = urlObj.pathname.split('.');
      if (pathnameParts.length > 1) {
        const originalExt = pathnameParts.pop()?.toLowerCase();
        if (originalExt && ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(originalExt)) {
          extension = originalExt;
        }
      }
    } catch (e) {
      console.warn('Could not parse image URL for extension:', e);
    }
    
    const fileName = `${cleanNameHint}-${Date.now()}.${extension}`;
    
    console.log(`Attempting to download image from ${imageUrl}`);
    
    // Download the image
    try {
      // Try to use a CORS proxy for WordPress sites that block direct access
      let fetchUrl = imageUrl;
      let fetchOptions: RequestInit = {
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
        }
      };
      
      // Check if this is likely a WordPress media URL that might have CORS issues
      const isWpMedia = imageUrl.includes('wp-content/uploads') || 
                        imageUrl.includes('edgefi.com') || 
                        imageUrl.includes('.cdn-website.com');
      
      if (isWpMedia) {
        console.log(`Using direct fetch with no-cors for WordPress image: ${imageUrl}`);
        
        // Try a direct fetch first
        try {
          const directResponse = await fetch(imageUrl, {
            headers: {
              'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
            }
          });
          
          if (directResponse.ok) {
            // If direct fetch works, use it
            console.log(`Direct fetch successful for: ${imageUrl}`);
            const imageBlob = await directResponse.blob();
            
            // Convert to File object for upload
            const file = new File([imageBlob], fileName, { type: imageBlob.type || `image/${extension}` });
            
            // Try uploading to Supabase if available
            if (supabase) {
              try {
                const uploadedUrl = await uploadImageToStorage(file, 'blog-imports');
                console.log(`Successfully uploaded image to Supabase: ${uploadedUrl}`);
                return uploadedUrl;
              } catch (uploadError) {
                console.error('Failed to upload to Supabase:', uploadError);
              }
            }
            
            // Fall back to data URL for testing without Supabase
            const base64 = await convertBlobToBase64(imageBlob);
            return base64;
          } else {
            console.log(`Direct fetch failed with status ${directResponse.status}, will try alternate methods`);
          }
        } catch (directError) {
          console.log(`Direct fetch failed with error: ${directError.message}`);
        }
        
        // For specific problematic domains, try to use a Supabase proxy function if available
        if (supabase) {
          try {
            console.log(`Attempting to use WordPress proxy for: ${imageUrl}`);
            
            // Use the WordPress proxy edge function
            const { data, error } = await supabase.functions.invoke('wordpress-proxy', {
              body: { 
                url: imageUrl,
                mode: 'image'
              }
            });
            
            if (!error && data && data.imageBase64) {
              console.log(`Successfully fetched image via proxy: ${imageUrl}`);
              // Convert base64 to blob
              const binaryString = atob(data.imageBase64);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              
              const imageBlob = new Blob([bytes], { type: `image/${extension}` });
              const file = new File([imageBlob], fileName, { type: `image/${extension}` });
              
              // Try uploading to Supabase
              const uploadedUrl = await uploadImageToStorage(file, 'blog-imports');
              console.log(`Successfully uploaded proxied image to Supabase: ${uploadedUrl}`);
              return uploadedUrl;
            } else {
              console.error('Proxy returned error or no image data:', error || 'No image data');
              // Continue with other methods
            }
          } catch (proxyError) {
            console.warn('Failed to use WordPress proxy:', proxyError);
          }
        }
        
        // If all else fails, use a placeholder
        console.log(`All methods failed, using placeholder for: ${imageUrl}`);
        return '/placeholder.svg';
      }
      
      // Standard fetch for non-WordPress images
      console.log(`Using standard fetch for: ${imageUrl}`);
      const response = await fetch(fetchUrl, fetchOptions);
      
      if (!response.ok) {
        throw new Error(`Failed to download image: ${response.status} ${response.statusText}`);
      }
      
      // Get the image blob
      const imageBlob = await response.blob();
      
      // If blob is empty or too small, it might not be a valid image
      if (imageBlob.size < 100) {
        console.warn(`Image blob size suspiciously small (${imageBlob.size} bytes), might be invalid`);
        return '/placeholder.svg';
      }
      
      // If Supabase is configured, try to upload there
      if (supabase) {
        try {
          const file = new File([imageBlob], fileName, { type: imageBlob.type || `image/${extension}` });
          const uploadedUrl = await uploadImageToStorage(file, 'blog-imports');
          console.log(`Uploaded image to Supabase: ${uploadedUrl}`);
          return uploadedUrl;
        } catch (uploadError) {
          console.error('Failed to upload to Supabase, falling back to base64:', uploadError);
          
          // Fall back to base64 data URL for testing
          const base64 = await convertBlobToBase64(imageBlob);
          return base64;
        }
      }
      
      // If no Supabase, convert to base64 for testing
      console.log(`No Supabase available, using base64 for: ${fileName}`);
      const base64 = await convertBlobToBase64(imageBlob);
      return base64;
    } catch (fetchError) {
      console.error('Error fetching image:', fetchError);
      
      // Return a placeholder image
      console.log(`Returning placeholder image for failed fetch: ${imageUrl}`);
      return '/placeholder.svg';
    }
  } catch (error) {
    console.error('Error in processImage:', error);
    return '/placeholder.svg'; // Return placeholder on error
  }
};

/**
 * Convert a blob to a base64 data URL
 */
const convertBlobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Process a logo image and remove its background
 * @param imageUrl The URL of the logo image to process
 * @returns The local URL of the processed logo image
 */
export const processLogoImage = async (imageUrl: string): Promise<string> => {
  try {
    // For logo processing, we'd typically remove the background
    // For now, just use the regular image processing function
    return await processImage(imageUrl, 'logo');
  } catch (error) {
    console.error('Error processing logo:', error);
    return imageUrl; // Fallback to original image if processing fails
  }
};

/**
 * Upload an image to Supabase storage
 * @param imageFile The image file to upload
 * @param path The storage path
 * @returns The URL of the uploaded image
 */
export const uploadImageToStorage = async (imageFile: File, path: string = 'blog'): Promise<string> => {
  if (!supabase) {
    console.error('Supabase client not initialized');
    throw new Error('Supabase client not initialized');
  }

  try {
    const fileName = `${path}/${Date.now()}_${imageFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    
    console.log(`Uploading ${imageFile.name} (${imageFile.size} bytes) to Supabase storage as ${fileName}`);
    
    // Check if the image file is valid (not empty)
    if (imageFile.size <= 0) {
      throw new Error('Image file is empty');
    }
    
    // Upload the image to Supabase Storage
    const { data, error } = await supabase.storage
      .from('blog-images')
      .upload(fileName, imageFile, {
        cacheControl: '3600',
        upsert: true,
      });
    
    if (error) {
      console.error('Supabase storage upload error:', error);
      throw error;
    }
    
    // Get the public URL of the uploaded image
    const { data: { publicUrl } } = supabase.storage
      .from('blog-images')
      .getPublicUrl(fileName);
    
    console.log(`Supabase storage upload successful, public URL: ${publicUrl}`);
    return publicUrl;
  } catch (error) {
    console.error('Error uploading image to storage:', error);
    throw error;
  }
};

/**
 * Detect if a URL is likely to cause CORS issues
 * @param url The URL to check
 * @returns True if the URL is likely to have CORS issues
 */
export const isLikelyCorsProblem = (url: string): boolean => {
  // Skip if not a valid URL
  if (!url || typeof url !== 'string') return false;
  
  try {
    const urlObj = new URL(url);
    
    // Check for common domains that may block cross-origin requests
    const problematicDomains = [
      'edgefi.com',
      'cdn-website.com',
      'wordpress.com',
      'wp.com',
      'wp-content',
      'blogspot.com',
      'squarespace.com',
      'wixsite.com',
      'webflow.com',
      'hubspot.com',
      'shopify.com',
      'images.unsplash.com',
      'amazonaws.com',
      'cloudfront.net'
    ];
    
    // Check if the URL contains any of the problematic domains
    return problematicDomains.some(domain => url.includes(domain));
  } catch (e) {
    // If the URL is invalid, assume it's not a CORS problem
    return false;
  }
};
